import React, { useState, useEffect } from 'react';
import { validateForm, commonValidations } from '../utils/validation';
import { productsService } from '../services';
import { computeAmount, computeUnitPrice, priceDelta } from '../utils/pricing';
import { formatCurrency } from '../utils/formatters';

const LOCATIONS = ['Accra', 'Kumasi'];

const EMPTY_FORM = {
    customerName: '',
    customerPhone: '',
    location: LOCATIONS[0],
    productId: '',
    quantity: 1,
    unitPrice: '',
    amount: '',
    notes: '',
    collectedBy: ''
};

const inputClass = (hasError, extra = '') =>
    `w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ${extra} ${
        hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'
    }`;

const labelClass = 'block text-sm font-medium text-slate-700 mb-1.5';

const FieldError = ({ message }) =>
    message ? (
        <p className="mt-1 text-sm text-red-600 flex items-center">
            <span className="mr-1">⚠️</span>
            {message}
        </p>
    ) : null;

const SalesForm = ({ onSubmit }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    // Which money field the user typed in last; the other one is derived from it.
    const [lastEdited, setLastEdited] = useState('unitPrice');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedProduct = products.find(p => p.id === formData.productId);
    const listPrice = selectedProduct ? Number(selectedProduct.price) : null;
    const delta = priceDelta(formData.unitPrice, listPrice);

    // Fetch products on component mount
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await productsService.getAll(1, 100); // Get all products
                setProducts(response.products || []);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Choosing a product starts from its catalogue price
    useEffect(() => {
        const product = products.find(p => p.id === formData.productId);
        if (product) {
            setLastEdited('unitPrice');
            setFormData(prev => ({ ...prev, unitPrice: String(product.price) }));
        }
    }, [formData.productId, products]);

    // Keep unit price and total consistent; the field edited last is the anchor
    useEffect(() => {
        setFormData(prev => {
            if (lastEdited === 'unitPrice') {
                const amount = computeAmount(prev.unitPrice, prev.quantity);
                return amount === prev.amount ? prev : { ...prev, amount };
            }
            const unitPrice = computeUnitPrice(prev.amount, prev.quantity);
            return unitPrice === prev.unitPrice ? prev : { ...prev, unitPrice };
        });
    }, [formData.unitPrice, formData.amount, formData.quantity, lastEdited]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'unitPrice' || name === 'amount') {
            setLastEdited(name);
        }

        if (name === 'customerPhone') {
            const cleanedValue = value.replace(/\D/g, '');
            if (cleanedValue.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: cleanedValue }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const applyListPrice = () => {
        if (!selectedProduct) return;
        setLastEdited('unitPrice');
        setFormData(prev => ({ ...prev, unitPrice: String(selectedProduct.price) }));
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setLastEdited('unitPrice');
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const validationErrors = validateForm(formData, commonValidations);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            await onSubmit({ ...formData, listPrice });
            resetForm();
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrors({ submit: 'Failed to submit sale. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Information Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Customer Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="customerName" className={labelClass}>Customer Name *</label>
                            <input
                                type="text"
                                id="customerName"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleChange}
                                required
                                placeholder="Enter customer name"
                                className={inputClass(errors.customerName)}
                            />
                            <FieldError message={errors.customerName} />
                        </div>

                        <div>
                            <label htmlFor="customerPhone" className={labelClass}>Customer Phone *</label>
                            <input
                                type="tel"
                                id="customerPhone"
                                name="customerPhone"
                                value={formData.customerPhone}
                                onChange={handleChange}
                                required
                                placeholder="Enter 10-digit phone number"
                                maxLength={10}
                                pattern="[0-9]{10}"
                                className={inputClass(errors.customerPhone)}
                            />
                            <FieldError message={errors.customerPhone} />
                        </div>
                    </div>
                </div>

                {/* Sale Details Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Sale Details
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="location" className={labelClass}>Location *</label>
                            <select
                                id="location"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                className={inputClass(errors.location)}
                                required
                            >
                                {LOCATIONS.map(location => (
                                    <option key={location} value={location}>{location}</option>
                                ))}
                            </select>
                            <FieldError message={errors.location} />
                        </div>

                        <div>
                            <label htmlFor="productId" className={labelClass}>Product *</label>
                            <select
                                id="productId"
                                name="productId"
                                value={formData.productId}
                                onChange={handleChange}
                                className={inputClass(errors.productId)}
                                required
                                disabled={loading}
                            >
                                <option value="">Select a product</option>
                                {products.map(product => (
                                    <option key={product.id} value={product.id}>
                                        {product.name} - GH₵{product.price} (Stock: {product.stock_count})
                                    </option>
                                ))}
                            </select>
                            {loading && <p className="mt-1 text-sm text-gray-500">Loading products...</p>}
                            <FieldError message={errors.productId} />
                        </div>

                        <div>
                            <label htmlFor="collectedBy" className={labelClass}>Collected By *</label>
                            <input
                                type="text"
                                id="collectedBy"
                                name="collectedBy"
                                value={formData.collectedBy}
                                onChange={handleChange}
                                required
                                placeholder="Enter collector's name"
                                className={inputClass(errors.collectedBy)}
                            />
                            <FieldError message={errors.collectedBy} />
                        </div>
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Pricing</h3>
                        {selectedProduct && (
                            <span className="text-sm text-gray-500">
                                List price: <span className="font-medium text-gray-700">{formatCurrency(listPrice)}</span>
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="quantity" className={labelClass}>Quantity *</label>
                            <input
                                type="number"
                                id="quantity"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleChange}
                                required
                                placeholder="Enter quantity"
                                min="1"
                                className={inputClass(errors.quantity)}
                            />
                            <FieldError message={errors.quantity} />
                        </div>

                        <div>
                            <label htmlFor="unitPrice" className={labelClass}>Price per unit (GH₵) *</label>
                            <input
                                type="number"
                                inputMode="decimal"
                                id="unitPrice"
                                name="unitPrice"
                                value={formData.unitPrice}
                                onChange={handleChange}
                                required
                                placeholder="Price per unit"
                                min="0"
                                step="0.01"
                                className={inputClass(errors.unitPrice)}
                            />
                            <FieldError message={errors.unitPrice} />
                        </div>

                        <div>
                            <label htmlFor="amount" className={labelClass}>Total amount (GH₵) *</label>
                            <input
                                type="number"
                                inputMode="decimal"
                                id="amount"
                                name="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                required
                                placeholder="Total, or type to override"
                                min="0"
                                step="0.01"
                                className={inputClass(errors.amount, 'font-semibold')}
                            />
                            <FieldError message={errors.amount} />
                        </div>
                    </div>

                    {/* Price summary */}
                    <div
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border p-4 ${
                            !delta
                                ? 'bg-gray-50 border-gray-200'
                                : delta.diff > 0
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-amber-50 border-amber-200'
                        }`}
                        aria-live="polite"
                    >
                        <div>
                            <div className="text-sm text-gray-500">Sale total</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {formData.amount !== '' ? formatCurrency(formData.amount) : '—'}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            {delta ? (
                                <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        delta.diff > 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                    }`}
                                >
                                    {delta.diff > 0 ? '▲' : '▼'} {formatCurrency(Math.abs(delta.diff))} per unit (
                                    {delta.percent > 0 ? '+' : ''}{delta.percent}%) vs list price
                                </span>
                            ) : (
                                selectedProduct && (
                                    <span className="text-sm text-gray-500">At list price</span>
                                )
                            )}
                            {delta && (
                                <button
                                    type="button"
                                    onClick={applyListPrice}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                                >
                                    Use list price
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        Change the price per unit and the total follows. Or type the total and the price per unit follows.
                    </p>
                </div>

                {/* Notes */}
                <div>
                    <label htmlFor="notes" className={labelClass}>Notes</label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder={delta ? 'Optional: why was the price changed?' : 'Optional notes about the sale'}
                        rows="3"
                        className={inputClass(errors.notes, 'resize-none')}
                    />
                    <FieldError message={errors.notes} />
                </div>

                {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <span className="text-red-600 mr-2">⚠️</span>
                            <span className="text-red-600 text-sm">{errors.submit}</span>
                        </div>
                    </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={resetForm}
                        className="w-full sm:w-auto h-12 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200"
                    >
                        Clear Form
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto h-12 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium ${
                            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Submitting...
                            </span>
                        ) : (
                            <span>Add Sale</span>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SalesForm;
